import {
    Avatar,
    Card,
    Grid,
    styled,
    Typography,
    TextField,
    Button,
    Snackbar,
    Alert,
} from "@mui/material";
import sideImgLearn from "assets/icons/learn.svg";
import { StyledButton } from "components/StyledButton";
import { UnauthenticatedLayout } from "components/UnauthenticatedLayout";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { routes } from "routes";
import { useKeycloak } from "@react-keycloak/web";

const PREFIX = "SignInPage";

const classes = {
    root: `${PREFIX}-root`,
    textField: `${PREFIX}-textField`,
    div: `${PREFIX}-div`,
    div2: `${PREFIX}-div2`,
    avatar: `${PREFIX}-avatar`,
    typography: `${PREFIX}-typography`,
    card: `${PREFIX}-card`,
};

const Root = styled(UnauthenticatedLayout)(({ theme }) => ({
    [`&.${classes.root}`]: {
        flexGrow: 1,
        minHeight: "70vh",
        minWidth: "70vw",
    },

    [`& .${classes.textField}`]: {
        margin: theme.spacing(1),
        width: "100%",
    },

    [`& .${classes.div}`]: {
        position: "relative",
        margin: 20,
    },

    [`& .${classes.div2}`]: {
        margin: 15,
        height: 15,
        fontSize: 14,
    },

    [`& .${classes.avatar}`]: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
    },

    [`& .${classes.typography}`]: {
        padding: theme.spacing(2),
    },

    [`& .${classes.card}`]: {
        border: 0,
        borderRadius: 10,
        padding: theme.spacing(10),
        margin: theme.spacing(1),
    },
}));

export const SignInPage = (): JSX.Element => {
    const { keycloak } = useKeycloak();
    const autoLoginTriggered = useRef(false);
    const location = useLocation();
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotStatus, setForgotStatus] = useState<"idle" | "loading" | "done">("idle");
    const [snackOpen, setSnackOpen] = useState(false);

    const handleForgotPassword = useCallback(async () => {
        if (!forgotEmail) return;
        setForgotStatus("loading");
        try {
            await fetch("/api/v1/auth/forgot-password", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: forgotEmail }),
            });
        } catch {
            // intentionally ignore — always show generic message
        }
        setForgotStatus("done");
        setSnackOpen(true);
    }, [forgotEmail]);
    
    const startKeycloakLogin = useCallback((): void => {
        const redirectUri = new URL(
            routes._.signIn.path,
            window.location.origin,
        ).toString();

        window.history.replaceState(null, document.title, routes._.signIn.path);
        void keycloak.login({ redirectUri });
    }, [keycloak]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const isKeycloakCallback =
            location.hash.includes("code=") ||
            location.hash.includes("state=") ||
            location.hash.includes("session_state=") ||
            location.hash.includes("error=");

        if (
            params.get("mode") === "keycloak" &&
            !autoLoginTriggered.current &&
            !isKeycloakCallback &&
            !keycloak.authenticated
        ) {
            autoLoginTriggered.current = true;
            startKeycloakLogin();
        }
    }, [
        keycloak,
        keycloak.authenticated,
        location.hash,
        location.search,
        startKeycloakLogin,
    ]);
    return (
        <Root className={classes.root}>
            <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                className={classes.root}
            >
                <Grid item xs={12} md={6}>
                    <Card className={classes.card}>
                        <Grid container justifyContent="center">
                            <Avatar className={classes.avatar} />
                        </Grid>
                        <Typography component="h5" variant="h5">
                            Sign In
                        </Typography>
                        <Typography sx={{ mt: 2, mb: 3 }}>
                            Sign in to access the Teacher Portal.
                        </Typography>
                        <Grid item>
                            <StyledButton
                                data-testid="keycloak-login"
                                onClick={startKeycloakLogin}
                                variant="contained"
                                color="primary"
                            >
                                Sign In
                            </StyledButton>
                        </Grid>
                        <Grid item>
                            <Typography variant="caption">
                                Is this your first time here?
                                <Link data-testid="register" to={routes._.register.path}>
                                    Sign Up
                                </Link>
                            </Typography>
                        </Grid>
                        <Grid item sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Forgot your password?
                            </Typography>
                            <TextField
                                size="small"
                                fullWidth
                                label="Enter your email"
                                type="email"
                                value={forgotEmail}
                                onChange={e => setForgotEmail(e.target.value)}
                                disabled={forgotStatus === "loading"}
                            />
                            <Button
                                sx={{ mt: 1 }}
                                variant="outlined"
                                size="small"
                                onClick={handleForgotPassword}
                                disabled={forgotStatus === "loading" || !forgotEmail}
                            >
                                {forgotStatus === "loading" ? "Sending..." : "Send Reset Email"}
                            </Button>
                        </Grid>
                    </Card>
                    <Snackbar open={snackOpen} autoHideDuration={6000} onClose={() => setSnackOpen(false)}>
                        <Alert onClose={() => setSnackOpen(false)} severity="success" sx={{ width: "100%" }}>
                            If that email is registered, a password reset link has been sent.
                        </Alert>
                    </Snackbar>
                </Grid>
                <Grid item xs={12} md={6}>
                    <img data-testid="sideImg" src={sideImgLearn} alt="teacher" />
                </Grid>
            </Grid>
        </Root>
    );
};
